<template>
  <div />
</template>

<script setup>
// Marks the tab itself while it is hidden: the number of new requests in front
// of the title, plus a dot on the favicon. Deliberately not behind either of the
// sound's gates — `claimNewRequest` lets a single tab play so the operator
// hears one beep instead of five, but every hidden tab has to carry the badge,
// since any of them may be the one they come back to, and
// `play_sound_notifications` mutes the sound, not the screen.

// favicon.ico's 16/32/48 frames with a #ff4242 dot (--color-danger-500, scaled
// per frame) in the top right. Redraw from favicon.ico if the logo changes.
const FAVICON_BADGED_URL = '/icons/favicon-badge.ico';
// What the browser falls back to on its own — the app declares no icon.
const FAVICON_PLAIN_URL = '/favicon.ico';
const appTitle = useRuntimeConfig().public.title;

// Channels fans new.request out per connection and can redeliver, and Vue
// coalesces watcher runs, so neither "one run, one request" nor "the last entry
// is the new one" holds.
const shownRequestIds = new Set();

// Refs
const visibility = useDocumentVisibility();
const newRequestsCount = ref(0);

// Computed
const newWebsocketRequests = computed(() => useNuxtApp().$store.state.operator.requests.newWebsocketRequests);

// Methods
const markAllShown = () => {
  newWebsocketRequests.value.forEach(({ id }) => shownRequestIds.add(id));
  newRequestsCount.value = 0;
};

// Through the title template rather than document.title, which every route
// change would wipe. The favicon rides in the same entry rather than on
// useFavicon, so clearing the count puts the plain one back.
useHead(() => {
  // Read out here: unhead skips titleTemplate when it walks the entry, so a count
  // read only inside the template is a dependency Vue never sees.
  const badgeCount = newRequestsCount.value;

  return {
    titleTemplate: (titleChunk) => {
      const title = titleChunk ? `${titleChunk} | ${appTitle}` : appTitle;

      return badgeCount ? `(${badgeCount}) ${title}` : title;
    },
    link: [{ rel: 'icon', href: badgeCount ? FAVICON_BADGED_URL : FAVICON_PLAIN_URL }],
  };
});

// Lifecycle
// Whatever arrived before this tab had a badge is not news to the operator.
onMounted(() => {
  markAllShown();
});

// Watchers
// Both directions say the same thing: on the way out everything already waiting
// is old news, on the way back the operator has just seen all of it.
watch(visibility, markAllShown);

watch(newWebsocketRequests, (requests) => {
  if (visibility.value !== 'hidden') { return }

  const filters = readOperatorRequestsFilter();

  requests.forEach((request) => {
    if (shownRequestIds.has(request.id)) { return }

    shownRequestIds.add(request.id);

    if (matchesOperatorRequestsFilter(request, filters)) {
      newRequestsCount.value += 1;
    }
  });
});
</script>
